import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { Query, useMutation } from 'react-apollo';
import { withStyles } from '@material-ui/styles';
import Spin from 'components/common/Spin';
import MilestoneDetails from './MilestoneDetails';
import {
  GET_STUDENT_MILESTONES,
  UPDATE_MILESTONE_EXERCISE,
} from 'graphql/student/milestones';
import Milestone from './Milestone';
import { API } from 'aws-amplify';
import useSnack from 'hooks/useSnack';
import StudentLCCalendar from 'modules/student/lead/Meeting';
import StudentHCCalendar from 'modules/student/head/Meeting';
import StudentLCCalendarCircle from 'modules/student/lead/CircleMeeting';
import StudentHCCalendarCircle from 'modules/student/head/CircleMeeting';
import FACalendar from 'modules/student/fa_coach/Meeting';
import FAFSAMeeting from 'modules/student/fa_coach/FAFSAmeeting';
import CSSProfileMeeting from 'modules/student/fa_coach/CSSProfileMeeting';
import CSSandFAFSAMeeting from 'modules/student/fa_coach/CSSandFAFSA';
import withConfirm from 'hooks/withConfirm';
import moment from 'moment-timezone';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import List from '@material-ui/core/List';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import ReferralCard from 'modules/referral/components/ReferralCard';
import Carousel from '../../student/timeline/components/Carousel';

const styles = (theme) => ({
  upcomingMilestones: {
    'cursor': 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
});

const Milestones = ({
  classes,
  disableScheduling,
  student,
  confirm,
  readOnly,
  disableView,
  location
}) => {
  const [updateTask] = useMutation(UPDATE_MILESTONE_EXERCISE);
  const snack = useSnack();
  const [selected, setSelected] = useState();
  const [showModal, toggleModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [listLength, setListLength] = useState(4);
  const user = useSelector((s) => s.user.user);
  const userId = student.user_id;

  const handleClick = (idx) => {
    setCurrentIndex(idx);
  };

  React.useEffect(() => {
    if(location?.state){
      setCurrentIndex(location?.state.currentIndex)
    }
  },[location]);

  const handleSeeMore = (total) => {
    let len = listLength + 4;
    if (len > total) setListLength(total);
    else setListLength(len);
  };

  const cancelMeeting = (task) => {
    return API.del('api', `/calendar/events/${task.response.eventId}`).then(
      () => {
        snack.success('Meeting canceled.');
        const payload = {
          id: task.id,
          input: {
            due_date: null,
            status: null,
            response: null,
          },
        };
        return updateTask({
          variables: payload,
        });
      },
      (e) => snack.error(e.message),
    );
  };

  const onRequestCounseling = (task) => {
    toggleModal({ task });
  };

  const onCancelMeeting = (task) => {
    if (task.response?.eventId) {
      confirm(() => cancelMeeting(task));
    } else {
      snack.info('This meeting cannot be canceled.');
    }
  };

  const renderModalContent = () => {
    if (!showModal) return null;
    const { task } = showModal;
    const type = task.exercise.meeting_type;
    const props = {
      task,
      student: {
        name: `${student.user_first_name} ${student.user_last_name}`,
        email: student.user_email,
        lc_id: student.lc_id,
        hc_id: student.hc_id,
        fa_id: student.fa_id,
      },
      allowCustomSlot: true,
      availablePeriods: {
        start: moment
          .utc()
          .add(1, 'd')
          .format(),
        end: moment
          .utc()
          .add(30, 'd')
          .format(),
      },
      onClose: () => toggleModal(),
      onBookingComplete: (event) => {
        return updateTask({
          variables: {
            id: task.id,
            input: {
              due_date: event.slot.start,
              status: 'inprogress',
              response: {
                sub: user.sub,
                eventId: event.id,
                source: 'google',
              },
            },
          },
        });
      },
    };

    const prefix = 'Book';
    if (type === 'cc') {
      return (
        <StudentLCCalendar {...props} title={`${prefix} Coaching Session`} />
      );
    } else if (type === 'hc') {
      return (
        <StudentHCCalendar {...props} title={`${prefix} Milestone Meeting`} />
      );
    } else if (type === 'fa') {
      return (
        <FACalendar {...props} title={`${prefix} Financial Aid Meeting`} />
      );
    } else if (type === 'circle_cc') {
      return (
        <StudentLCCalendarCircle
          {...props}
          title={`${prefix} Coaching Session`}
        />
      );
    } else if (type === 'circle_hc') {
      return (
        <StudentHCCalendarCircle
          {...props}
          title={`${prefix} Milestone Meeting`}
        />
      );
    } else if (type === 'fafsa') {
      return <FAFSAMeeting {...props} title={`FAFSA Review Meeting`} />;
    } else if (type === 'css_profile') {
      return (
        <CSSProfileMeeting {...props} title={`CSS Profile Review Meeting`} />
      );
    } else if (type === 'fafsa_css') {
      return (
        <CSSandFAFSAMeeting
          {...props}
          title={`FAFSA and CSS Profile Review Meeting`}
        />
      );
    }
  };

  return (
    <div>
      <Query
        query={GET_STUDENT_MILESTONES}
        fetchPolicy={'cache-and-network'}
        variables={{ userId }}
      >
        {({ data, loading }) => {
          if (!data) {
            return <Spin spinning />;
          }

          const milestones = data?.milestones;
          const totalMilestones = milestones?.length || 0;
          const doneMilestones =
            totalMilestones > 0
              ? milestones.filter((f) => f.status === 'done').length
              : 0;
          const upcomingMilestones =
            totalMilestones > 0
              ? milestones.filter(
                  (f) => f.status === null && f.name !== 'FA Session',
                ).length
              : 0;

          return (
            <Grid container spacing={3}>
              <Helmet title="Timeline" />
              <Grid item xs={12} sm={6} md={8}>
                <Typography paragraph variant="h5">
                  Your progress
                </Typography>
                <Carousel
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  totalMilestones={totalMilestones}
                  doneMilestones={doneMilestones}
                  milestones={data.milestones}
                  user={user}
                  student={student}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h5">Upcoming Milestones</Typography>
                <List>
                  {data.milestones
                    .filter((f) => f.status === null && f.name !== 'FA Session')
                    .map(
                      (i, key) =>
                        key < listLength && (
                          <ListItem
                            className={classes.upcomingMilestones}
                            onClick={() => handleClick(i.order)}
                            divider
                            key={i.id}
                            disableGutters
                          >
                            <ListItemText
                              primary={i.name}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondary={`${i.exercises.length} Exercises`}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ),
                    )}
                </List>
                {listLength < upcomingMilestones && (
                  <Button
                    fullWidth
                    size="large"
                    color="primary"
                    variant="outlined"
                    onClick={() => handleSeeMore(upcomingMilestones)}
                  >
                    See more
                  </Button>
                )}
                {user.type === "parent" && (
                  <Box marginTop={3}>
                    <ReferralCard />
                  </Box>
                )}
              </Grid>
              {selected && (
                <MilestoneDetails
                  onClose={() => setSelected()}
                  milestone={selected}
                  studentId={userId}
                  student={student}
                  user={user}
                />
              )}
            </Grid>
          );
        }}
      </Query>
      {renderModalContent()}
    </div>
  );
};

export default withStyles(styles)(withConfirm(Milestones));
